import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';

/** Reads the effective user selected by an administrator from the connected route. */
@Injectable({ providedIn: 'root' })
export class AdminConnectionContextService {
  private readonly router: Router = inject(Router);
  private readonly connectedRoleSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private connectedOfficeId: string | null = null;

  /** Emits the selected account role while administrator connection mode is active. */
  public readonly connectedRole$: Observable<string | null> = this.connectedRoleSubject.asObservable();

  /** Returns the connected user ID only while the current URL is inside the admin connection workspace. */
  public getConnectedUserId(url: string = this.router.url): string | null {
    const path: string = url.split(/[?#]/, 1)[0];
    const segments: string[] = path.split('/').filter((segment: string): boolean => segment !== '');
    const connectIndex: number = segments.findIndex(
      (segment: string, index: number): boolean => segment === 'connect' && segments[index - 1] === 'admin'
    );
    const encodedUserId: string | undefined = segments[connectIndex + 1];

    if (connectIndex < 0 || encodedUserId === undefined || encodedUserId === '') {
      return null;
    }

    try {
      return decodeURIComponent(encodedUserId);
    } catch {
      return null;
    }
  }

  /** Updates the role used by shared navigation while the connected account shell is active. */
  public setConnectedRole(role: string | null): void {
    this.connectedRoleSubject.next(role);
  }

  /** Returns the latest selected account role for synchronous shared-header rendering. */
  public getConnectedRole(): string | null {
    return this.connectedRoleSubject.value;
  }

  /** Updates the office used by connected Doctor-only header actions. */
  public setConnectedOfficeId(officeId: string | null): void {
    this.connectedOfficeId = officeId;
  }

  /** Returns the selected Doctor's office while administrator connection mode is active. */
  public getConnectedOfficeId(): string | null {
    return this.connectedOfficeId;
  }
}
